import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Loader2, FileText, Table, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentPreview = ({ document, token, onClose, onDownload }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [r2Url, setR2Url] = useState(null);
  const [legacyData, setLegacyData] = useState(null);

  // Prevent background scrolling when preview is open
  useEffect(() => {
    const docEl = window.document;
    if (docEl?.body) docEl.body.style.overflow = 'hidden';
    return () => { if (docEl?.body) docEl.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const fetchPreview = async () => {
      setLoading(true);
      setError(null);
      setR2Url(null);
      setLegacyData(null);

      const fileType = document.file_type || '';
      const isViewableInBrowser = fileType === 'application/pdf' || fileType.startsWith('image/');

      try {
        // Step 1: For PDFs and images, try R2 presigned URL (instant, browser-rendered)
        if (isViewableInBrowser) {
          const urlResp = await fetch(`${API_URL}/api/documents/${document.document_id}/preview-url`, {
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (urlResp.ok) {
            const data = await urlResp.json();
            if (data.url) {
              setR2Url(data.url);
              setLoading(false);
              return;
            }
          }
        }

        // Step 2: Fall back to legacy preview endpoint (for text/Excel/Word/MongoDB-stored)
        const response = await fetch(`${API_URL}/api/documents/${document.document_id}/preview`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Failed to load preview');
        }

        const contentType = response.headers.get('content-type');

        if (contentType?.startsWith('image/') || contentType === 'application/pdf') {
          // Legacy blob fallback for R2-disabled deployments
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setR2Url(url);
        } else {
          const data = await response.json();
          setLegacyData(data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (document) fetchPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document, token]);

  const fileType = document?.file_type || '';
  const isPdf = fileType === 'application/pdf';
  const isImage = fileType.startsWith('image/');

  const renderPreview = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center h-96" data-testid="preview-loading">
          <Loader2 className="w-12 h-12 text-primary animate-spin mb-4" />
          <p className="text-white/60">Loading preview...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-96">
          <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
          <p className="text-red-400 mb-2">Failed to load preview</p>
          <p className="text-white/60 text-sm mb-4">{error}</p>
          <Button onClick={() => onDownload(document)} className="bg-primary text-black hover:bg-primary/90">
            <Download className="w-4 h-4 mr-2" /> Download Instead
          </Button>
        </div>
      );
    }

    // PDF — render directly via <iframe> from R2 signed URL (no decryption wait)
    if (isPdf && r2Url) {
      return (
        <div className="w-full h-full" style={{ minHeight: '70vh' }}>
          <iframe
            src={r2Url}
            title={document.name}
            className="w-full h-full bg-white rounded-sm"
            style={{ minHeight: '70vh', border: 'none' }}
            data-testid="pdf-preview-iframe"
          />
        </div>
      );
    }

    // Image — render directly via <img> from R2 signed URL
    if (isImage && r2Url) {
      return (
        <div className="flex items-center justify-center p-4 overflow-auto max-h-[80vh]">
          <img
            src={r2Url}
            alt={document.name}
            className="max-w-full max-h-full object-contain rounded-sm"
            data-testid="image-preview"
          />
        </div>
      );
    }

    if (!legacyData) return null;

    // Text preview
    if (legacyData.type === 'text') {
      return (
        <div className="overflow-auto max-h-[70vh]">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-primary" />
            <span className="text-white/60 text-sm">
              {legacyData.format === 'docx' ? 'Word Document' : 'Text Content'}
            </span>
          </div>
          <pre className="bg-black/50 p-4 rounded-sm text-white/80 text-sm whitespace-pre-wrap font-mono overflow-auto">
            {legacyData.content}
          </pre>
        </div>
      );
    }

    // Table preview (Excel)
    if (legacyData.type === 'table') {
      return (
        <div className="overflow-auto max-h-[70vh]">
          <div className="flex items-center gap-2 mb-3">
            <Table className="w-4 h-4 text-primary" />
            <span className="text-white/60 text-sm">
              Showing {legacyData.rows?.length} of {legacyData.total_rows} rows
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-primary/20">
                  {legacyData.headers?.map((header, idx) => (
                    <th key={idx} className="px-4 py-2 text-left text-white text-sm font-semibold border border-white/10">{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {legacyData.rows?.map((row, rowIdx) => (
                  <tr key={rowIdx} className="hover:bg-white/5">
                    {row.map((cell, cellIdx) => (
                      <td key={cellIdx} className="px-4 py-2 text-white/80 text-sm border border-white/10">
                        {cell !== null && cell !== undefined ? String(cell) : '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Unsupported
    if (legacyData.type === 'unsupported') {
      return (
        <div className="flex flex-col items-center justify-center h-96">
          <FileText className="w-16 h-16 text-white/20 mb-4" />
          <p className="text-white/60 mb-2">{legacyData.message}</p>
          <p className="text-white/40 text-sm mb-6">File type: {legacyData.file_type || document.file_type}</p>
          <Button onClick={() => onDownload(document)} className="bg-primary text-black hover:bg-primary/90">
            <Download className="w-4 h-4 mr-2" /> Download to View
          </Button>
        </div>
      );
    }

    return null;
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-[#050505] flex flex-col" data-testid="document-preview-overlay">
      {/* Header — always visible */}
      <div className="flex items-center justify-between p-3 border-b border-white/10 bg-[#0A0A0A] flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <FileText className="w-4 h-4 text-primary flex-shrink-0" />
          <h3 className="text-white font-medium text-sm truncate" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{document.name}</h3>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {r2Url && (isPdf || isImage) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open(r2Url, '_blank', 'noopener,noreferrer')}
              className="text-white/80 hover:bg-white/10 h-9 px-2 text-xs"
              data-testid="preview-open-tab-btn"
              title="Open in new tab"
            >
              <ExternalLink className="w-4 h-4" />
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onDownload(document)} className="border-primary/40 text-primary hover:bg-primary/10 h-9 px-3 text-xs font-semibold" data-testid="preview-download-btn">
            <Download className="w-4 h-4 mr-1.5" /> Download
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose} className="text-white/80 hover:bg-white/10 h-9 w-9 p-0" data-testid="preview-close-btn">
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        {(isPdf || isImage) && r2Url ? renderPreview() : <div className="p-2 sm:p-4">{renderPreview()}</div>}
      </div>
    </div>,
    window.document.body
  );
};

export default DocumentPreview;
